import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BxButtonComponent, BxFileUploadComponent, BxFileUploadStatus, BxInputComponent } from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NotificationsService } from '../../../services/notifications/notifications.service';
import { calculatePercentage } from '../../../utils/utils';
import { EiImportService } from './ei-import.service';

@Component({
  selector: 'bxi-ei-import',
  imports: [BxFileUploadComponent, TranslatePipe, BxButtonComponent, BxInputComponent, ReactiveFormsModule],
  templateUrl: './ei-import.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EiImportComponent {
  private readonly eiImportService = inject(EiImportService);
  private readonly notifications = inject(NotificationsService);
  private readonly translate = inject(TranslateService);

  protected readonly filesToUpload = signal<FileList | null>(null);
  protected readonly errorKey = signal<string | null>(null);
  protected readonly uploading = signal<boolean>(false);
  protected readonly fileUploadStatus = signal<BxFileUploadStatus | undefined>(undefined);

  protected readonly kek = new FormControl('');

  protected onFileChanged(files: FileList | null): void {
    this.errorKey.set(null);
    this.filesToUpload.set(files);
  }

  protected importFile(file: File): void {
    this.uploading.set(true);
    this.errorKey.set(null);
    const kekValue = this.kek.value;
    if (!kekValue) {
      return;
    }

    this.eiImportService.uploadElsFile(file, kekValue).subscribe({
      next: (event: HttpEvent<unknown>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            this.fileUploadStatus.set({ [file.name]: { progress: calculatePercentage(event.loaded, event.total ?? 1) } });
            break;
          case HttpEventType.Response:
            if (event.status === 200) {
              this.filesToUpload.set(null);
              this.notifications.addSuccessNotification(
                this.translate.instant('system.info.importSuccess'),
                this.translate.instant('system.info.elsUpload', { fileName: file.name }),
              );
            } else {
              this.fileUploadStatus.set({
                [file.name]: { errorMessage: this.translate.instant('system.errors.elsUpload') },
              });
              this.errorKey.set('system.errors.elsUpload');
            }
            break;
        }
      },
      error: (err) => {
        console.error(err.message);
        this.errorKey.set('system.errors.elsUpload');
        this.uploading.set(false);
      },
      complete: () => {
        this.uploading.set(false);
      },
    });
  }
}
